import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  Save, RefreshCw, Globe, Bell, Shield, Users,
  Mail, Phone, MapPin, Building2, CreditCard,
  Palette, Type, Hash, ToggleLeft, Download,
  Upload, Trash2, Eye, EyeOff, Lock, Key,
  UserCog, Settings as SettingsIcon, HelpCircle,
  FileText, Clock, Calendar, BarChart, PieChart,
  AlertTriangle, CheckCircle, XCircle, Info,
  Database  // ← AJOUTE CETTE LIGNE
} from 'lucide-react';

export default function Settings() {
  const [settings, setSettings] = useState({
    company: {
      name: 'CRM Pro Center',
      email: 'contact@crmpro.com',
      phone: '+33 1 23 45 67 89',
      address: '123 Avenue de la République, 75011 Paris',
      siret: '123 456 789 00012',
      website: 'https://crmpro.com',
      logo: null
    },
    appearance: {
      theme: 'dark',
      primaryColor: '#06b6d4',
      accentColor: '#3b82f6',
      fontSize: 'medium',
      animations: true,
      compactMode: false
    },
    notifications: {
      emailAlerts: true,
      pushNotifications: false,
      slackWebhook: '',
      dailyDigest: true,
      weeklyReport: true,
      alertOnNewLead: true,
      alertOnDealWon: true,
      alertOnTaskDue: true
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: 30,
      maxLoginAttempts: 5,
      ipWhitelist: [],
      passwordPolicy: 'strong',
      auditLogs: true
    },
    localization: {
      language: 'fr',
      timezone: 'Europe/Paris',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h',
      currency: 'EUR',
      firstDayOfWeek: 'monday'
    },
    data: {
      autoBackup: true,
      backupFrequency: 'daily',
      retentionDays: 30,
      exportFormat: 'csv'
    }
  });

  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [testMode, setTestMode] = useState(false);

  const tabs = [
    { id: 'general', label: 'Général', icon: SettingsIcon },
    { id: 'company', label: 'Entreprise', icon: Building2 },
    { id: 'appearance', label: 'Apparence', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Sécurité', icon: Shield },
    { id: 'localization', label: 'Localisation', icon: Globe },
    { id: 'data', label: 'Données', icon: Database }
  ];

  const handleSave = async () => {
    setSaving(true);
    // Simuler une sauvegarde
    await new Promise(resolve => setTimeout(resolve, 1500));
    setSaving(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleReset = () => {
    if (window.confirm('Réinitialiser tous les paramètres ?')) {
      window.location.reload();
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = 'crm-settings.json';
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          setSettings(imported);
          alert('Paramètres importés avec succès !');
        } catch (err) {
          alert('Erreur lors de l\'import du fichier');
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Paramètres
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gérez la configuration de votre CRM
          </p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-800 text-gray-300 hover:text-white hover:border-gray-700 transition flex items-center gap-2"
          >
            <Download size={18} />
            Exporter
          </button>
          <label className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-800 text-gray-300 hover:text-white hover:border-gray-700 transition flex items-center gap-2 cursor-pointer">
            <Upload size={18} />
            Importer
            <input type="file" accept=".json" onChange={handleImport} className="hidden" />
          </label>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-800 text-gray-300 hover:text-red-400 hover:border-red-900 transition flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Réinitialiser
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-cyan-500/20 transition flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save size={18} />
                Sauvegarder
              </>
            )}
          </button>
        </div>
      </div>

      {saveSuccess && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="text-green-400" size={20} />
          <p className="text-green-400">Paramètres sauvegardés avec succès !</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-800 pb-4">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-cyan-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
        
        <button
          onClick={() => setTestMode(!testMode)}
          className={`ml-auto px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
            testMode ? 'bg-yellow-600 text-white' : 'bg-gray-800 text-gray-400'
          }`}
        >
          {testMode ? <Eye size={18} /> : <EyeOff size={18} />}
          Mode test
        </button>
      </div>

      {/* Contenu des onglets */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-xl border border-gray-800 p-6">
        {activeTab === 'general' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Paramètres généraux</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Nom du CRM</label>
                  <input
                    type="text"
                    value="CRM Pro Center"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Version</label>
                  <input
                    type="text"
                    value="3.0.0 (Stable)"
                    disabled
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-gray-400"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Environnement</label>
                  <select className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none">
                    <option>Production</option>
                    <option>Staging</option>
                    <option>Development</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">URL du site</label>
                  <input
                    type="text"
                    value="https://crm.procenter.com"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email support</label>
                  <input
                    type="email"
                    value="support@crmpro.com"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="maintenance" className="w-4 h-4 rounded border-gray-600 bg-gray-800" />
                  <label htmlFor="maintenance" className="text-sm text-gray-300">Mode maintenance</label>
                </div>
              </div>
            </div>
            
            <div className="pt-4 border-t border-gray-800">
              <h3 className="text-sm font-medium text-white mb-3">Actions rapides</h3>
              <div className="flex flex-wrap gap-3">
                <button className="px-4 py-2 bg-gray-800 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition">
                  Vider le cache
                </button>
                <button className="px-4 py-2 bg-gray-800 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition">
                  Reindexer la recherche
                </button>
                <button className="px-4 py-2 bg-gray-800 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition">
                  Nettoyer les logs
                </button>
                <button className="px-4 py-2 bg-gray-800 rounded-lg text-sm text-gray-300 hover:bg-red-400/10 hover:text-red-400 transition">
                  Réinitialiser tout
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'company' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Informations entreprise</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Nom de l'entreprise</label>
                  <input
                    type="text"
                    value={settings.company.name}
                    onChange={(e) => setSettings({...settings, company: {...settings.company, name: e.target.value}})}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email</label>
                  <input
                    type="email"
                    value={settings.company.email}
                    onChange={(e) => setSettings({...settings, company: {...settings.company, email: e.target.value}})}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={settings.company.phone}
                    onChange={(e) => setSettings({...settings, company: {...settings.company, phone: e.target.value}})}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">SIRET</label>
                  <input
                    type="text"
                    value={settings.company.siret}
                    onChange={(e) => setSettings({...settings, company: {...settings.company, siret: e.target.value}})}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Adresse</label>
                  <textarea
                    value={settings.company.address}
                    onChange={(e) => setSettings({...settings, company: {...settings.company, address: e.target.value}})}
                    rows="3"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Site web</label>
                  <input
                    type="url"
                    value={settings.company.website}
                    onChange={(e) => setSettings({...settings, company: {...settings.company, website: e.target.value}})}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Logo</label>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 bg-gray-800 rounded-lg border border-gray-700 flex items-center justify-center">
                      <Building2 className="text-gray-600" size={32} />
                    </div>
                    <button className="px-4 py-2 bg-gray-800 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition">
                      Changer le logo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'appearance' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Apparence</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Thème</label>
                  <select className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white">
                    <option>Sombre (par défaut)</option>
                    <option>Clair</option>
                    <option>System</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Couleur primaire</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value="#06b6d4"
                      className="w-12 h-10 bg-gray-800 border border-gray-700 rounded-lg"
                    />
                    <input
                      type="text"
                      value="#06b6d4"
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Taille de police</label>
                  <select className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white">
                    <option>Petite</option>
                    <option selected>Moyenne</option>
                    <option>Grande</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Animations</p>
                    <p className="text-xs text-gray-500">Activer les transitions et effets</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Mode compact</p>
                    <p className="text-xs text-gray-500">Réduire les espacements</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Autres onglets similaires... */}
      </div>
    </div>
  );
}